// UPPDRAG-20: canonical "unpaid" definition shared between the dashboard
// count card (ManagerDashboard) and the unpaid-list page (DashboardUnpaid).
//
// Before this hook, the two surfaces had silently-drifting filter logic
// AND the list page query was completely broken (PostgREST 400 on
// `profiles:user_id(...)` because the FK was missing — fixed in the
// 20260428120000 migration). User saw "53 obetalda" on the dashboard
// and "0 — Alla betalningar hanterade!" on the list page.
//
// Canonical filter (use BOTH the count + items hook to guarantee they
// agree):
//   - payment.status IN ('pending', 'failed')
//   - bookings.status != 'cancelled'  (exclude void bookings)
//   - bookings.courts.hall_id = $hallId  (hall-scoped)
//   - bookings.user_id != organization.walk_in_user_id  (exclude
//     walk-in admin-cash bookings — payment is settled at the desk)
//
// Plus org-scoped memberships:
//   - memberships.status IN ('past_due', 'unpaid')
//   - memberships.organization_id = $orgId

import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useOrganization } from "./useOrganization";

export interface UnpaidItem {
  id: string;
  type: "booking" | "membership";
  paymentId: string;
  userName: string;
  userId: string;
  description: string;
  /** Sprint D aggregates: store in öre for accurate sum + display. */
  amountMinor: number;
  currency: string;
  createdAt: string;
  /** Only set on `type === "booking"`. */
  bookingId?: string;
  bookingStartTime?: string;
  bookingStatus?: string;
  courtName?: string;
  /** Only set on `type === "membership"`. */
  membershipId?: string;
  membershipPlanName?: string;
  membershipPeriodEnd?: string | null;
  /** Payment status (pending or failed) — useful for badge variants in the list UI. */
  paymentStatus?: "pending" | "failed";
}

const UNPAID_BOOKING_STATUSES = ["pending", "failed"] as const;
const UNPAID_MEMBERSHIP_STATUSES = ["past_due", "unpaid"] as const;

// ─────────────────────────────────────────────────────────────────────
// Dashboard count-only variant: avoids fetching row payloads. Returns
// the SAME canonical count as useUnpaidPayments().items.length.
// ─────────────────────────────────────────────────────────────────────
export function useUnpaidCount(hallId: string | null | undefined): {
  count: number;
  isLoading: boolean;
} {
  const { organization } = useOrganization();
  const walkInUserId = organization?.walk_in_user_id;
  const orgId = organization?.id;

  const bookingsCountQuery = useQuery({
    queryKey: ["unpaid-count-bookings", hallId, walkInUserId],
    queryFn: async () => {
      const { count } = await supabase
        .from("booking_payments")
        .select("*, bookings!inner(status, courts!inner(hall_id))", {
          count: "exact",
          head: true,
        })
        .in("status", [...UNPAID_BOOKING_STATUSES])
        .eq("bookings.courts.hall_id", hallId!)
        .neq("bookings.status", "cancelled")
        .neq("user_id", walkInUserId!);
      return count ?? 0;
    },
    enabled: !!hallId && !!walkInUserId,
  });

  const membershipsCountQuery = useQuery({
    queryKey: ["unpaid-count-memberships", orgId],
    queryFn: async () => {
      const { count } = await supabase
        .from("memberships")
        .select("*", { count: "exact", head: true })
        .eq("organization_id", orgId!)
        .in("status", [...UNPAID_MEMBERSHIP_STATUSES]);
      return count ?? 0;
    },
    enabled: !!orgId,
  });

  const total =
    (bookingsCountQuery.data ?? 0) + (membershipsCountQuery.data ?? 0);
  return {
    count: total,
    isLoading: bookingsCountQuery.isLoading || membershipsCountQuery.isLoading,
  };
}

// ─────────────────────────────────────────────────────────────────────
// Full data variant: fetches all unpaid items with embedded profile +
// booking/court info for the list-page UI. Same filter as the count
// hook so the count card and list-page header always agree.
// ─────────────────────────────────────────────────────────────────────
export function useUnpaidPayments(hallId: string | null | undefined): {
  items: UnpaidItem[];
  isLoading: boolean;
  refetchAll: () => void;
} {
  const { organization } = useOrganization();
  const walkInUserId = organization?.walk_in_user_id;
  const orgId = organization?.id;

  const bookingsQuery = useQuery({
    queryKey: ["unpaid-bookings", hallId, walkInUserId],
    queryFn: async (): Promise<UnpaidItem[]> => {
      // Important: profiles:user_id requires booking_payments_user_id_profiles_fkey
      // (added in migration 20260428120000). Without that FK this query
      // returns HTTP 400 and React Query swallows the error.
      // Single-string select (no concat) so supabase-js can do literal-string
      // type inference on the embedded relationships. Splitting via `+` returns
      // GenericStringError and breaks downstream property access.
      const { data, error } = await supabase
        .from("booking_payments")
        .select("id, booking_id, amount, amount_minor, currency, created_at, user_id, status, profiles:user_id(full_name), bookings!inner(start_time, end_time, status, courts!inner(name, hall_id))")
        .in("status", [...UNPAID_BOOKING_STATUSES])
        .eq("bookings.courts.hall_id", hallId!)
        .neq("bookings.status", "cancelled")
        .neq("user_id", walkInUserId!)
        .order("created_at", { ascending: true });
      if (error) throw error;
      return (data ?? []).map((p): UnpaidItem => {
        const profile = p.profiles as unknown as { full_name: string | null } | null;
        const booking = p.bookings as unknown as {
          start_time: string;
          end_time: string;
          status: string;
          courts: { name: string; hall_id: string } | null;
        } | null;
        const courtName = booking?.courts?.name ?? "—";
        // Sprint D dual-state: prefer amount_minor.
        const amountMinor =
          (p as { amount_minor?: number | null }).amount_minor ??
          (p.amount != null ? Math.round(p.amount * 100) : 0);
        return {
          id: p.id,
          type: "booking",
          paymentId: p.id,
          userName: profile?.full_name ?? "Okänd",
          userId: p.user_id,
          description: `Bokning: ${courtName}`,
          amountMinor,
          currency: (p as { currency?: string }).currency ?? "SEK",
          createdAt: p.created_at,
          bookingId: p.booking_id,
          bookingStartTime: booking?.start_time,
          bookingStatus: booking?.status,
          courtName,
          paymentStatus: p.status as "pending" | "failed",
        };
      });
    },
    enabled: !!hallId && !!walkInUserId,
  });

  const membershipsQuery = useQuery({
    queryKey: ["unpaid-memberships", orgId],
    queryFn: async (): Promise<UnpaidItem[]> => {
      // profiles:user_id needs memberships_user_id_profiles_fkey
      // (added in migration 20260428120000).
      const { data, error } = await supabase
        .from("memberships")
        .select("id, user_id, plan_id, status, current_period_end, created_at, profiles:user_id(full_name), membership_plans:plan_id(name, price, price_minor, currency)")
        .eq("organization_id", orgId!)
        .in("status", [...UNPAID_MEMBERSHIP_STATUSES]);
      if (error) throw error;
      return (data ?? []).map((m): UnpaidItem => {
        const profile = m.profiles as unknown as { full_name: string | null } | null;
        const plan = m.membership_plans as unknown as {
          name: string;
          price: number;
          price_minor: number | null;
          currency: string;
        } | null;
        const amountMinor =
          plan?.price_minor ??
          (plan?.price != null ? Math.round(plan.price * 100) : 0);
        return {
          id: m.id,
          type: "membership",
          paymentId: m.id,
          userName: profile?.full_name ?? "Okänd",
          userId: m.user_id,
          description: `Medlemskap: ${plan?.name ?? "—"}`,
          amountMinor,
          currency: plan?.currency ?? "SEK",
          createdAt: m.created_at,
          membershipId: m.id,
          membershipPlanName: plan?.name,
          membershipPeriodEnd: m.current_period_end,
        };
      });
    },
    enabled: !!orgId,
  });

  const items: UnpaidItem[] = [
    ...(bookingsQuery.data ?? []),
    ...(membershipsQuery.data ?? []),
  ];
  const isLoading = bookingsQuery.isLoading || membershipsQuery.isLoading;
  const refetchAll = () => {
    bookingsQuery.refetch();
    membershipsQuery.refetch();
  };

  return { items, isLoading, refetchAll };
}
